import { DealStep } from "../graphql/generated";
import {
  FolderUpIcon,
  ListChecksIcon,
  MessageCircleQuestionMarkIcon,
  SquarePenIcon,
  UserSearchIcon,
} from "lucide-react";
import { classNames } from "../utils/cn";

// Define the order of deal steps
const DEAL_STEPS_ORDER: DealStep[] = [
  DealStep.GatheringInformation,
  DealStep.GeneratingMarketing,
  DealStep.FindingBuyers,
  DealStep.Negotiations,
  DealStep.Closed,
];

const DEAL_STEP_LABELS: Record<DealStep, string> = {
  [DealStep.GatheringInformation]: "Gathering Information",
  [DealStep.GeneratingMarketing]: "Preparing materials",
  [DealStep.FindingBuyers]: "Finding Buyers",
  [DealStep.Negotiations]: "Diligence",
  [DealStep.Closed]: "Closing",
};

type StepStatus = "completed" | "current" | "upcoming";

function getStepStatus(step: DealStep, currentStep: DealStep): StepStatus {
  const stepIndex = DEAL_STEPS_ORDER.indexOf(step);
  const currentIndex = DEAL_STEPS_ORDER.indexOf(currentStep);

  if (stepIndex < currentIndex) {
    return "completed";
  } else if (stepIndex === currentIndex) {
    return "current";
  }
  return "upcoming";
}

export function DealStepTimeline(props: { currentStep: DealStep }) {
  const { currentStep } = props;
  const currentIndex = DEAL_STEPS_ORDER.indexOf(currentStep);
  const stepCount = DEAL_STEPS_ORDER.length;
  const centerPercent = (index: number) => ((index + 0.5) / stepCount) * 100;
  const currentCenter = centerPercent(currentIndex);
  const gradientStart = currentIndex > 0 ? centerPercent(currentIndex - 1) : 0;
  const completedWidth = Math.max(0, gradientStart);
  const gradientWidth = Math.max(0, currentCenter - gradientStart);

  return (
    <nav aria-label="Deal Progress">
      <div className="relative">
        <div className="absolute left-0 right-0 top-4 h-0.5 bg-gray-300" />
        <div
          className="absolute left-0 top-4 h-0.5 bg-persian-950"
          style={{ width: `${completedWidth}%` }}
        />
        <div
          className="absolute top-4 h-0.5 bg-gradient-to-r from-persian-950 to-persian-500"
          style={{ left: `${gradientStart}%`, width: `${gradientWidth}%` }}
        />
      </div>
      <ol className="relative z-10 flex items-start">
        {DEAL_STEPS_ORDER.map((step, index) => {
          const status = getStepStatus(step, currentStep);

          return (
            <li key={step} className="relative flex-1">
              <div className="flex w-full flex-col items-center">
                <StepIndicator step={step} status={status} />
                <StepLabel step={step} status={status} />
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function StepIndicator(props: { step: DealStep; status: StepStatus }) {
  const { status, step } = props;
  const iconColor =
    status === "completed" ? "text-persian-950" : status === "current" ? "text-persian-600" : "text-gray-400";
  const containerClass =
    status === "completed"
      ? "bg-white border-persian-950"
      : status === "current"
        ? "bg-white border-persian-500"
        : "bg-white border-gray-300";

  return (
    <div className={classNames("flex h-8 w-8 items-center justify-center rounded-full border-2", containerClass)}>
      <StepIcon step={step} className={classNames("h-4 w-4", iconColor)} />
    </div>
  );
}

function StepLabel(props: { step: DealStep; status: StepStatus }) {
  const { step, status } = props;

  return (
    <p
      className={classNames(
        "mt-2 text-center text-xs font-medium w-full",
        status === "current"
          ? "text-persian-600"
          : status === "completed"
            ? "text-persian-950"
            : "text-gray-400"
      )}
    >
      {DEAL_STEP_LABELS[step]}
    </p>
  );
}

function StepIcon(props: { step: DealStep; className?: string }) {
  const { step, className } = props;

  switch (step) {
    case DealStep.GatheringInformation:
      return <FolderUpIcon className={className} />;
    case DealStep.GeneratingMarketing:
      return <SquarePenIcon className={className} />;
    case DealStep.FindingBuyers:
      return <UserSearchIcon className={className} />;
    case DealStep.Negotiations:
      return <MessageCircleQuestionMarkIcon className={className} />;
    case DealStep.Closed:
      return <ListChecksIcon className={className} />;
    default:
      return <FolderUpIcon className={className} />;
  }
}

// Utility to check if deal is at or past a certain step
export function isAtOrPastStep(
  currentStep: DealStep,
  targetStep: DealStep
): boolean {
  const currentIndex = DEAL_STEPS_ORDER.indexOf(currentStep);
  const targetIndex = DEAL_STEPS_ORDER.indexOf(targetStep);
  return currentIndex >= targetIndex;
}
